<template>
    <div>
        <!-- Postcode Lookup field -->
        <postcode-lookup-field
            class="contentsquare_sensitive_data"
            address-type="shipping"
            v-model="address.postcode"
            @clear-address="clearAddress"
            @address-selected="updateSelectedAddress">
        </postcode-lookup-field>
        <div class="input input--invalid" v-if="showIsPostcodeDeliverableValidationMessage">
            <p class="input__message" v-text="$t('GIFTING_ADDRESS_FORM_REALLY_SORRY')"></p>
        </div>

        <!-- Address line 1 input field -->
        <div :class="addressLine1InputClass">
            <label class="input__label" for="shipping-address-line1"
                v-text="$t('DELIVERY_ADDRESS_FIRST_LINE')"></label>
            <input
                id="shipping-address-line1"
                name="shipping-address-line1"
                autocomplete="shipping address-line1"
                type="text"
                class="input__field contentsquare_sensitive_data"
                v-model.lazy="address.addressLine1">

            <!-- Validation -->
            <p class="input__message" v-if="showAddressLine1RequiredValidationMessage"
                v-text="$t('DELIVERY_ADDRESS_PLEASE_PUT_IN')"></p>
        </div>

        <!-- Address line 2 input field -->
        <div :class="addressLine2InputClass">
            <label class="input__label" for="shipping-address-line2"
                v-text="$t('DELIVERY_ADDRESS_SECOND_LINE')"></label>
            <input
                id="shipping-address-line2"
                name="shipping-address-line2"
                autocomplete="shipping address-line2"
                type="text"
                class="input__field contentsquare_sensitive_data"
                v-model="address.addressLine2"
                placeholder="Optional">
        </div>

        <!-- Address city input field -->
        <div :class="addressCityInputClass">
            <label class="input__label" for="shipping-city"
                v-text="$t('DELIVERY_ADDRESS_TOWN_OR_CITY')"></label>
            <input
                id="shipping-city"
                name="shipping-city"
                autocomplete="shipping address-level2"
                type="text"
                class="input__field contentsquare_sensitive_data"
                v-model.lazy="address.city">

            <!-- Validation -->
            <p class="input__message" v-if="showAddressCityRequiredValidationMessage"
                v-text="$t('DELIVERY_ADDRESS_PLEASE_PUT_TOWN_OR_CITY')"></p>
        </div>
    </div>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import { isPostcodeDeliverable, isValidPostcode } from '@/validators/PostcodeValidator';
import PostcodeLookupField from '@/components/forms/fields/PostcodeLookupField';

export default {
    name: 'GiftingAddressForm',
    props: {
        enableDeliveryWaitingList: {
            default: false,
            type: Boolean,
        },
        value: {
            default: null,
            type: Object,
        },
    },
    data() {
        return {
            apiValidation: {
                postcode: {
                    isPostcodeDeliverable: true,
                },
            },
            address: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            isDeliveryWaitingListModalVisible: false,
        };
    },
    computed: {
        addressCityInputClass() {
            if (this.address.city === null) {
                return 'input';
            }

            return this.isAddressCityValid ? 'input input--success' : 'input input--invalid';
        },

        addressLine1InputClass() {
            if (this.address.addressLine1 === null) {
                return 'input';
            }

            return this.isAddressLine1Valid ? 'input input--success' : 'input input--invalid';
        },

        addressLine2InputClass() {
            return this.address?.addressLine2?.length > 0 ? 'input input--success' : 'input';
        },

        isAddressCityValid() {
            return !this.$v.address.city.$invalid;
        },

        isAddressLine1Valid() {
            return !this.$v.address.addressLine1.$invalid;
        },

        isAllFormFieldsValid() {
            return !this.$v.$invalid;
        },

        isPostcodeValid() {
            return this.$v.address.postcode.required && this.$v.address.postcode.isValidPostcode;
        },

        showAddressCityRequiredValidationMessage() {
            return this.address.city !== null && !this.$v.address.city.required;
        },

        showAddressLine1RequiredValidationMessage() {
            return this.address.addressLine1 !== null && !this.$v.address.addressLine1.required;
        },

        showIsPostcodeDeliverableValidationMessage() {
            return !this.$v.address.postcode.isPostcodeDeliverable && !this.enableDeliveryWaitingList;
        },
    },
    methods: {
        async canDeliveryToPostcode(postcode) {
            if (this.isPostcodeValid) {
                let status = await isPostcodeDeliverable(postcode);

                this.updateApiValidationStatus(status);
            }
        },

        clearAddress(value) {
            if (value) {
                this.address.addressLine1 = null;
                this.address.addressLine2 = null;
                this.address.city = null;
            }
        },

        updateApiValidationStatus(status) {
            this.apiValidation.postcode.isPostcodeDeliverable = status;
        },

        updateInput(deliveryAddress) {
            this.$v.$touch();

            this.$emit('input', deliveryAddress);
            this.$emit('update:validation', this.isAllFormFieldsValid);
        },

        updateSelectedAddress(selectedAddress) {
            if (selectedAddress) {
                this.address.addressLine1 = selectedAddress.addressline1;
                this.address.addressLine2 = selectedAddress.addressline2;
                this.address.city = selectedAddress.posttown;
            } else {
                this.address.addressLine1 = null;
                this.address.addressLine2 = null;
                this.address.city = null;
            }
        },
    },
    validations: {
        address: {
            addressLine1: {
                required,
            },
            city: {
                required,
            },
            postcode: {
                required,
                isValidPostcode,
                isPostcodeDeliverable() {
                    return this.apiValidation.postcode.isPostcodeDeliverable;
                },
            },
        },
    },
    watch: {
        address: {
            handler: function(deliveryAddress) {
                this.updateInput(deliveryAddress);
            },
            deep: true,
        },
        'address.postcode'(value) {
            this.canDeliveryToPostcode(value);
        },
    },
    components: {
        PostcodeLookupField,
    },
};
</script>
